import { useInfiniteQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import type { TodoItemResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

const LIMIT = 20;

interface UseCompletedTimelineOptions {
  listId?: string;
  slug?: string;
  completedByUserId?: string;
}

interface UseCompletedTimelineReturn {
  items: TodoItemResponse[];
  loading: boolean;
  error: string | null;
  loadMore: () => void;
  hasMore: boolean;
}

export function useCompletedTimeline(
  options: UseCompletedTimelineOptions,
): UseCompletedTimelineReturn {
  const { listId, slug, completedByUserId } = options;

  const queryKey = slug
    ? queryKeys.todoLists.publicCompleted(slug)
    : queryKeys.todoLists.completed(listId!, completedByUserId ? { completedByUserId } : undefined);

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey,
      queryFn: async ({ pageParam = 0 }) => {
        if (slug) {
          return api.getPublicTodoListCompleted(slug, { limit: LIMIT, offset: pageParam });
        }
        return api.getTodoListCompleted(listId!, {
          limit: LIMIT,
          offset: pageParam,
          completedByUserId,
        });
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const nextOffset = allPages.length * LIMIT;
        return nextOffset < lastPage.total ? nextOffset : undefined;
      },
      enabled: !!slug || !!listId,
    });

  const items = data?.pages.flatMap((p) => p.items) ?? [];

  return {
    items,
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load completed items' : null,
    loadMore: () => {
      if (hasNextPage && !isFetchingNextPage) fetchNextPage();
    },
    hasMore: !!hasNextPage,
  };
}
